import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Input, Modal, Select, Space, Statistic, Table, Tag, message } from 'antd';
import { Line } from '@ant-design/plots';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { get } from '../../api/request';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import './AIManagement.css';

const { RangePicker } = DatePicker;

interface AIHistoryItem {
  id: number;
  user_id?: number;
  username?: string;
  nickname?: string;
  conversation_type: string;
  provider: string;
  model: string;
  request_text: string;
  response_text: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  request_chars: number;
  response_chars: number;
  estimated_cost: string | number;
  currency: string;
  status: number;
  error_message?: string;
  created_at?: string;
}

interface Summary {
  count: number;
  cost: string | number;
  avg_cost: string | number;
  total_tokens: number;
}

interface StatsResponse {
  today: Summary;
  month: Summary;
  range: Summary & {
    max_single_cost: string | number;
    max_daily_cost: string | number;
    avg_daily_cost: string | number;
    top_day?: string;
  };
  trend: {
    date: string;
    count: number;
    cost: string | number;
    avg_cost: string | number;
    total_tokens: number;
  }[];
}

const TYPE_OPTIONS = [
  { label: 'AI创作', value: 'AI创作' },
  { label: '声音生成', value: '声音生成' },
];

const formatMoney = (value?: string | number) => `¥${Number(value || 0).toFixed(6)}`;

const AICostMonitorPage: React.FC = () => {
  const [dataSource, setDataSource] = useState<AIHistoryItem[]>([]);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [userId, setUserId] = useState<string>('');
  const [type, setType] = useState<string | undefined>();
  const [status, setStatus] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState<AIHistoryItem | null>(null);

  const queryParams = useMemo(() => ({
    page,
    page_size: pageSize,
    keyword,
    user_id: userId,
    conversation_type: type,
    status,
    date_start: dateRange?.[0]?.format('YYYY-MM-DDT00:00:00'),
    date_end: dateRange?.[1]?.format('YYYY-MM-DDT23:59:59'),
  }), [dateRange, keyword, page, pageSize, status, type, userId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [listData, statsData] = await Promise.all([
        get<any>(buildApiUrl(API_ENDPOINTS.ai_history.list, queryParams)),
        get<StatsResponse>(buildApiUrl(API_ENDPOINTS.ai_history.stats, queryParams)),
      ]);
      setDataSource(listData.data || []);
      setTotal(listData.total || 0);
      setStats(statsData);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载AI费用数据失败');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = () => {
    setPage(1);
    loadData();
  };

  const chartData = (stats?.trend || []).map((item) => ({
    date: item.date,
    value: Number(item.cost || 0),
  }));

  const columns: ColumnsType<AIHistoryItem> = [
    {
      title: '用户',
      key: 'user',
      width: 160,
      render: (_, record) => (
        <div>
          <div className="question-title">{record.nickname || record.username || '匿名用户'}</div>
          <div className="question-code">ID: {record.user_id || '-'}</div>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'conversation_type',
      width: 100,
      render: (value) => <Tag color={value === '声音生成' ? 'magenta' : 'blue'}>{value}</Tag>,
    },
    { title: '模型', dataIndex: 'model', width: 150 },
    {
      title: 'Token',
      dataIndex: 'total_tokens',
      width: 100,
      render: (value) => value || 0,
    },
    {
      title: '费用',
      dataIndex: 'estimated_cost',
      width: 120,
      render: (value) => formatMoney(value),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value) => <Tag color={value === 1 ? 'green' : 'red'}>{value === 1 ? '成功' : '失败'}</Tag>,
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 180,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, record) => <Button type="link" icon={<EyeOutlined />} onClick={() => setDetail(record)}>详情</Button>,
    },
  ];

  return (
    <div className="ai-management-page">
      <h1 className="page-title">费用监控</h1>

      <div className="ai-stat-grid">
        <Card bordered={false}><Statistic title="今日费用" value={formatMoney(stats?.today.cost)} /></Card>
        <Card bordered={false}><Statistic title="本月费用" value={formatMoney(stats?.month.cost)} /></Card>
        <Card bordered={false}><Statistic title="区间平均" value={formatMoney(stats?.range.avg_cost)} /></Card>
        <Card bordered={false}><Statistic title="区间最高单次" value={formatMoney(stats?.range.max_single_cost)} /></Card>
        <Card bordered={false}><Statistic title="区间最高日" value={formatMoney(stats?.range.max_daily_cost)} /></Card>
        <Card bordered={false}><Statistic title="日均费用" value={formatMoney(stats?.range.avg_daily_cost)} /></Card>
      </div>

      <Card bordered={false} className="ai-section-card" title="费用趋势">
        <Line
          data={chartData}
          xField="date"
          yField="value"
          height={260}
          point={{ size: 4 }}
          tooltip={{ formatter: (datum: any) => ({ name: '费用', value: formatMoney(datum.value) }) }}
        />
      </Card>

      <Card bordered={false}>
        <div className="table-toolbar">
          <Space wrap>
            <Input
              allowClear
              placeholder="搜索用户/内容"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
            />
            <Input
              allowClear
              placeholder="用户ID"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              style={{ width: 120 }}
            />
            <Select allowClear placeholder="类型" value={type} onChange={setType} options={TYPE_OPTIONS} style={{ width: 130 }} />
            <Select
              allowClear
              placeholder="状态"
              value={status}
              onChange={setStatus}
              options={[{ label: '成功', value: 1 }, { label: '失败', value: 0 }]}
              style={{ width: 110 }}
            />
            <RangePicker value={dateRange} onChange={(value) => setDateRange(value as [Dayjs, Dayjs] | null)} />
            <Button icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </Card>

      <Modal title="AI对话详情" open={!!detail} onCancel={() => setDetail(null)} footer={null} width={820}>
        {detail && (
          <div className="ai-history-detail">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="用户">{detail.nickname || detail.username || '匿名用户'}</Descriptions.Item>
              <Descriptions.Item label="类型">{detail.conversation_type}</Descriptions.Item>
              <Descriptions.Item label="模型">{detail.model}</Descriptions.Item>
              <Descriptions.Item label="总Token">{detail.total_tokens}</Descriptions.Item>
              <Descriptions.Item label="输入Token">{detail.prompt_tokens}</Descriptions.Item>
              <Descriptions.Item label="输出Token">{detail.completion_tokens}</Descriptions.Item>
              <Descriptions.Item label="费用">{formatMoney(detail.estimated_cost)}</Descriptions.Item>
              <Descriptions.Item label="时间">{detail.created_at ? new Date(detail.created_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
            </Descriptions>
            <div className="ai-result-block">
              <div className="form-section-title">用户内容</div>
              <div className="ai-result-text">{detail.request_text}</div>
            </div>
            <div className="ai-result-block">
              <div className="form-section-title">AI返回</div>
              <div className="ai-result-text">{detail.response_text}</div>
            </div>
            {detail.error_message && (
              <div className="ai-result-block">
                <div className="form-section-title">错误信息</div>
                <div className="ai-result-text">{detail.error_message}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AICostMonitorPage;
